// VerdictChip — colored status chip + plain headline + "judged against" line.
//
// Load-bearing: unknown/stale (and any unrecognized status) render as the
// dignified slate NEUTRAL tone, NOT error red. Never a bare word.
import React from "react";

import type { VerdictResolution } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { VERDICT_TONE_COLORS } from "./honestyTokens";

export interface VerdictChipProps {
  resolution: VerdictResolution;
  /** hide the headline/basis lines and render only the chip. */
  chipOnly?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  on_target: "On target",
  elevated: "Elevated",
  fault: "Fault",
  stale: "Stale",
  cold: "Cold",
  unknown: "Unknown",
};

// ───────────────────────────────────────────────────────────────────
// ME-CARDS-R1 A4 — the verdict BASIS seam
// ───────────────────────────────────────────────────────────────────
// `verdict.basis` is a PAYLOAD string (English). Interpolating it raw into the
// translated "Hodnotené voči {{basis}}" prefix produced a half-Slovak sentence
// with an invisible seam between chrome and data ("Hodnotené voči figure values
// (no calibrated threshold this milestone)").
//
// The rule: KNOWN-STRING MAP + QUOTED-VERBATIM FALLBACK. The closed set of basis
// strings the bot emits this milestone is exactly one, so it maps to a real
// translation. Anything else renders VERBATIM inside quotation marks in mono —
// visibly quoted bot data, never half-translated chrome, and never
// machine-translated by us. This map is CLIENT render chrome and deliberately
// lives here, NOT in the pure engine.
const KNOWN_BASIS_KEYS: Record<string, { key: string; en: string }> = {
  "figure values (no calibrated threshold this milestone)": {
    key: "verdict.basis.figureValuesNoThreshold",
    en: "figure values (no calibrated threshold this milestone)",
  },
};

/** Locale-appropriate quotation marks for verbatim bot data. */
const QUOTES: Record<string, [string, string]> = {
  sk: ["„", "“"], // „…"
  cs: ["„", "“"],
  en: ["“", "”"], // "…"
};

export const VerdictChip: React.FC<VerdictChipProps> = ({ resolution, chipOnly }) => {
  const { translate, getCurrentLanguage } = useTranslation("machineEvidence");
  const tone = VERDICT_TONE_COLORS[resolution.tone];
  const label = translate(`verdict.status.${resolution.status}`, {
    defaultValue: STATUS_LABELS[resolution.status] ?? "Unknown",
  }) as string;
  // The payload headline renders as-is (bot data); only the client "Status
  // unknown" FALLBACK is translated. The basis is payload data interpolated into
  // the translated "Judged against {basis}" prefix — no double-translation.
  const headlineText = resolution.headlineIsFallback
    ? (translate("verdict.statusUnknown", { defaultValue: "Status unknown" }) as string)
    : resolution.headline;
  // A4: a KNOWN basis is a real translation interpolated into the existing
  // prefix; an UNKNOWN one is rendered verbatim in locale quotes + mono, so the
  // chrome/data seam is visible instead of reading as broken Slovak.
  const known = resolution.basis ? KNOWN_BASIS_KEYS[resolution.basis] : undefined;
  const knownBasisLine =
    resolution.basis && known
      ? (translate("verdict.judgedAgainst", {
          basis: translate(known.key, { defaultValue: known.en }) as string,
          defaultValue: "Judged against {{basis}}",
        }) as string)
      : null;
  const langKey = (getCurrentLanguage() || "en").toLowerCase().split("-")[0];
  const [openQuote, closeQuote] = QUOTES[langKey] ?? QUOTES.en;
  const verbatimBasis =
    resolution.basis && !known ? `${openQuote}${resolution.basis}${closeQuote}` : null;
  const basisPrefix = verbatimBasis
    ? (translate("verdict.judgedAgainstPrefix", { defaultValue: "Judged against" }) as string)
    : null;

  return (
    <div className="msc-verdict" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        className={`msc-verdict-chip msc-verdict-${resolution.status}`}
        data-verdict-tone={resolution.tone}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          borderRadius: dt.radiusPill,
          fontFamily: dt.fontMono,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          color: tone.fg,
          background: tone.bg,
          border: `1px solid ${tone.border}`,
        }}
      >
        {label}
      </span>
      {!chipOnly && (
        <>
          <span style={{ color: dt.textPrimary, fontSize: 14, fontWeight: 500 }}>
            {headlineText}
          </span>
          {resolution.detail ? (
            <span style={{ color: dt.textSecondary, fontSize: 13 }}>{resolution.detail}</span>
          ) : null}
          {knownBasisLine ? (
            <span
              className="msc-verdict-basis"
              data-basis-kind="known"
              style={{ color: dt.textMuted, fontSize: 12, fontStyle: "italic" }}
            >
              {knownBasisLine}
            </span>
          ) : verbatimBasis ? (
            <span
              className="msc-verdict-basis"
              data-basis-kind="verbatim"
              style={{ color: dt.textMuted, fontSize: 12, fontStyle: "italic" }}
            >
              {basisPrefix}{" "}
              <span style={{ fontFamily: dt.fontMono }}>{verbatimBasis}</span>
            </span>
          ) : null}
        </>
      )}
    </div>
  );
};

export default VerdictChip;
